import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Creators as npsActions } from "store/nps/nps-duck";
import { readConfigFile } from "utils/appConfig";
import { saveKeycloakTokenForNps } from "utils/auth/localStorage";
import TokenService from "utils/auth/Token.service";
import YmsNotification from "utils/functions/notification";
import useTranslation from "utils/hooks/useTranslation";

interface INPSDispatchProps {
  npsGetKeycloakTokenAsync?: () => void;
  keycloakToken?: string;
}

function decodeToken(token) {
  if (!token) {
    return null;
  }
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(window.atob(base64));
}

const clearAutoCloseTimeout = (autoCloseTimeoutRef) => {
  if (autoCloseTimeoutRef.current !== null) {
    clearTimeout(autoCloseTimeoutRef.current);
    autoCloseTimeoutRef.current = null;
  }
};

const createRemoveDialogHandler = (originalRemoveDialog, autoCloseTimeoutRef) =>
  function customRemoveDialog(...args) {
    clearAutoCloseTimeout(autoCloseTimeoutRef);
    localStorage.setItem("hasAnsweredNps", "true");
    return originalRemoveDialog.apply(this, args);
  };

const waitForNpsDialog = (autoCloseTimeoutRef) => {
  const waitForNps = setInterval(() => {
    if (window.nps?.removeDialog) {
      const originalRemoveDialog = window.nps.removeDialog;
      window.nps.removeDialog = createRemoveDialogHandler(originalRemoveDialog, autoCloseTimeoutRef);
      clearInterval(waitForNps);
    }
  }, 300);

  return waitForNps;
};

const closeNpsModalOnTimeout = (autoCloseTimeoutRef, npsTimeout, isDisposed) => {
  if (isDisposed()) {
    autoCloseTimeoutRef.current = null;
    return;
  }

  if (document.getElementById("ambevtech-nps")) {
    YmsNotification({
      type: "error",
      description: npsTimeout,
    });
    if (window.nps?.removeDialog) {
      window.nps.removeDialog(true);
      localStorage.setItem("hasAnsweredNps", "true");
    }
  }
  autoCloseTimeoutRef.current = null;
};

const watchNpsModal = (autoCloseTimeoutRef, npsTimeout, isDisposed) => {
  const THREE_MINUTES = 3 * 60 * 1000;
  const modalCheckInterval = setInterval(() => {
    const npsModal = document.getElementById("ambevtech-nps");
    if (npsModal && autoCloseTimeoutRef.current === null) {
      autoCloseTimeoutRef.current = setTimeout(() => closeNpsModalOnTimeout(autoCloseTimeoutRef, npsTimeout, isDisposed), THREE_MINUTES);
      clearInterval(modalCheckInterval);
    }
  }, 500);

  return modalCheckInterval;
};

const cleanupNps = (waitForNps, modalCheckInterval, autoCloseTimeoutRef) => {
  const existing = document.getElementById("nps-widget-script");
  if (existing) {
    existing.remove();
  }
  clearInterval(waitForNps);
  clearInterval(modalCheckInterval);
  clearAutoCloseTimeout(autoCloseTimeoutRef);
};

export const NpsWidget = ({ npsGetKeycloakTokenAsync, keycloakToken }: INPSDispatchProps) => {
  const NPS_TIMEOUT = useTranslation({ id: "nps_timeout_answered_message" });
  const autoCloseTimeoutRef = useRef(null);

  useEffect(() => {
    npsGetKeycloakTokenAsync();
  }, [npsGetKeycloakTokenAsync]);

  useEffect(() => {
    let disposed = false;
    let cleanup: (() => void) | undefined;

    if (keycloakToken) {
      saveKeycloakTokenForNps(keycloakToken);
    }

    const initializeNps = async () => {
      try {
        const tokenService = new TokenService();
        const config = await readConfigFile();
        if (keycloakToken) {
          const userData = decodeToken(tokenService.getToken());
          if (!userData) return;
          const srcUrl = config.VITE_NEW_NPS_API_ENDPOINT;
          const environment = config.VITE_YMS_WEBSITE;
          const metadata = `${userData.locationDescription}, ${environment}, ${userData.role}`;
          const script = document.createElement("script");

          script.id = "nps-widget-script";
          script.src = srcUrl;
          script.async = true;
          script.setAttribute("productId", "15");
          script.setAttribute("productName", "YMS");
          script.setAttribute("auth", "keycloakToken");
          script.setAttribute("storage", "local");
          script.setAttribute("metadata", metadata);

          document.head.appendChild(script);
        }

        const waitForNps = waitForNpsDialog(autoCloseTimeoutRef);
        const modalCheckInterval = watchNpsModal(autoCloseTimeoutRef, NPS_TIMEOUT, () => disposed);

        const npsCleanup = () => cleanupNps(waitForNps, modalCheckInterval, autoCloseTimeoutRef);
        if (disposed) {
          npsCleanup();
          return;
        }
        cleanup = npsCleanup;
      } catch (error) {
        console.error(error);
      }
    };

    void initializeNps();

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [keycloakToken]);

  return null;
};

const mapStateToProps = (state) => ({
  keycloakToken: state.Nps.keycloakToken,
});

const mapActionsToProps = (dispatch) => bindActionCreators({ ...npsActions }, dispatch);

export default connect(mapStateToProps, mapActionsToProps)(NpsWidget);
